import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useTheme } from '../context/ThemeContext';
import { systemStatusService, pdfReportService } from '../api';
import aiModelService from '../api/services/aiModelService';
import { Button, ThemedText, ErrorState } from '../components';
import { ExternalLink, MoreHorizontal, Plug, Shield, Terminal, Wrench, Copy, Check, Search, ChevronDown, ChevronRight } from 'lucide-react';
import { StatusPalette, ReportStatusColors, SentryLevelColors, withOpacity } from '../constants/statusColors';
import { PageContainer, PageHeaderRow as PageHeader } from '../components/PageStyles';
import { ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter } from '../components/ModalStyles';
import VendorIcon from '../components/VendorIcon';
import ModelPicker from '../components/ModelPicker';

// --- Helpers ---

const formatUptime = (seconds) => {
  if (seconds == null) return 'N/A';
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  const parts = [];
  if (d > 0) parts.push(`${d}d`);
  if (h > 0) parts.push(`${h}h`);
  if (m > 0) parts.push(`${m}m`);
  parts.push(`${s}s`);
  return parts.join(' ');
};

const formatBytes = (bytes) => {
  if (bytes == null) return 'N/A';
  if (bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${units[i]}`;
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return 'N/A';
  return new Date(dateStr).toLocaleString();
};

const formatTimeAgo = (dateStr) => {
  if (!dateStr) return 'N/A';
  const diff = (Date.now() - new Date(dateStr).getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

const formatTokenPrice = (price) => {
  if (!price && price !== 0) return '—';
  if (price === 0) return 'Free';
  if (price < 1) return `$${price.toFixed(3)}`;
  return `$${price.toFixed(2)}`;
};

// --- Styled Components ---

const TabRow = styled.div`
  display: flex;
  gap: ${p => p.theme.spacing.sm};
  margin-bottom: ${p => p.theme.spacing.lg};
  border-bottom: 2px solid ${p => p.theme.colors.border};
  overflow-x: auto;
`;

const Tab = styled.button`
  padding: ${p => p.theme.spacing.sm} ${p => p.theme.spacing.lg};
  background: none;
  border: none;
  border-bottom: 2px solid ${p => p.active ? p.theme.colors.tint : 'transparent'};
  color: ${p => p.active ? p.theme.colors.tint : p.theme.colors.muted};
  font-weight: ${p => p.active ? '600' : '400'};
  cursor: pointer;
  margin-bottom: -2px;
  font-size: 14px;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    color: ${p => p.theme.colors.tint};
  }
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(420px, 1fr));
  gap: ${p => p.theme.spacing.lg};
`;

const Card = styled.div`
  background-color: ${p => p.theme.colors.backgroundSecondary};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.lg};
  overflow: hidden;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${p => p.theme.spacing.lg};
  border-bottom: 1px solid ${p => p.theme.colors.border};
`;

const CardTitle = styled.div`
  display: flex;
  align-items: center;
  gap: ${p => p.theme.spacing.md};
`;

const StatusDot = styled.span`
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: ${p =>
    p.$status === 'healthy' || p.$status === 'ok' ? StatusPalette.green :
    p.$status === 'loading' ? StatusPalette.amber :
    StatusPalette.red};
`;

const CardBody = styled.div`
  padding: ${p => p.theme.spacing.lg};
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid ${p => p.theme.colors.border}20;

  &:last-child {
    border-bottom: none;
  }
`;

const InfoLabel = styled.span`
  color: ${p => p.theme.colors.muted};
  font-size: 13px;
  font-weight: 500;
`;

const InfoValue = styled.span`
  color: ${p => p.theme.colors.text};
  font-size: 13px;
  font-weight: 600;
  text-align: right;
  max-width: 60%;
  word-break: break-word;
`;

const ErrorMessage = styled.div`
  padding: ${p => p.theme.spacing.lg};
  text-align: center;
  color: ${p => p.theme.colors.error || StatusPalette.red};
  font-size: 14px;
`;

const LastChecked = styled.div`
  padding: ${p => p.theme.spacing.sm} ${p => p.theme.spacing.lg};
  font-size: 11px;
  color: ${p => p.theme.colors.muted};
  text-align: right;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
`;

const Thead = styled.thead`
  background-color: ${p => p.theme.colors.backgroundSecondary};
`;

const Th = styled.th`
  text-align: left;
  padding: 10px 12px;
  font-weight: 600;
  color: ${p => p.theme.colors.muted};
  border-bottom: 2px solid ${p => p.theme.colors.border};
  white-space: nowrap;
`;

const Td = styled.td`
  padding: 10px 12px;
  border-bottom: 1px solid ${p => p.theme.colors.border}40;
  color: ${p => p.theme.colors.text};
  vertical-align: top;
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  ${p => {
    const map = { ...ReportStatusColors, ok: StatusPalette.green };
    const c = map[p.$variant] || StatusPalette.slate;
    return `background-color: ${withOpacity(c)}; color: ${c};`;
  }}
`;

const SummaryBar = styled.div`
  display: flex;
  gap: ${p => p.theme.spacing.lg};
  padding: ${p => p.theme.spacing.md} ${p => p.theme.spacing.lg};
  background-color: ${p => p.theme.colors.backgroundSecondary};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.lg};
  margin-bottom: ${p => p.theme.spacing.lg};
  flex-wrap: wrap;
`;

const SummaryItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${p => p.theme.spacing.sm};
  font-size: 14px;
  color: ${p => p.theme.colors.text};
`;

const SummaryCount = styled.span`
  font-weight: 700;
  font-size: 18px;
  color: ${p => p.$color || p.theme.colors.text};
`;

const TableContainer = styled.div`
  background-color: ${p => p.theme.colors.backgroundSecondary};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.lg};
  overflow-x: auto;
`;

const SectionTitle = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: ${p => p.theme.colors.text};
  padding: ${p => p.theme.spacing.md} 0 ${p => p.theme.spacing.sm} 0;
  margin-top: ${p => p.theme.spacing.md};

  &:first-child {
    margin-top: 0;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${p => p.theme.spacing.sm};
`;

const EmptyState = styled.div`
  padding: ${p => p.theme.spacing.xl};
  text-align: center;
  color: ${p => p.theme.colors.muted};
  font-size: 14px;
`;

const TruncatedText = styled.span`
  max-width: 300px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: inline-block;
`;

const FilterBar = styled.div`
  display: flex;
  align-items: center;
  gap: ${p => p.theme.spacing.md};
  margin-bottom: ${p => p.theme.spacing.lg};
  flex-wrap: wrap;
`;

const FilterSelect = styled.select`
  padding: 6px 12px;
  border-radius: ${p => p.theme.borderRadius.md};
  border: 1px solid ${p => p.theme.colors.border};
  background-color: ${p => p.theme.colors.backgroundSecondary};
  color: ${p => p.theme.colors.text};
  font-size: 13px;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${p => p.theme.colors.tint};
  }
`;

const SearchInput = styled.input`
  padding: 6px 12px;
  border-radius: ${p => p.theme.borderRadius.md};
  border: 1px solid ${p => p.theme.colors.border};
  background-color: ${p => p.theme.colors.backgroundSecondary};
  color: ${p => p.theme.colors.text};
  font-size: 13px;
  min-width: 180px;

  &:focus {
    outline: none;
    border-color: ${p => p.theme.colors.tint};
  }

  &::placeholder {
    color: ${p => p.theme.colors.muted};
  }
`;

const ActionButton = styled.button`
  padding: 3px 8px;
  border-radius: ${p => p.theme.borderRadius.sm || '4px'};
  border: 1px solid ${p => p.theme.colors.border};
  background-color: ${p =>
    p.$variant === 'resolve' ? `${StatusPalette.green}15` :
    p.$variant === 'ignore' ? `${StatusPalette.amber}15` :
    p.$variant === 'details' ? (p.theme.colors.tint + '15') :
    'transparent'};
  color: ${p =>
    p.$variant === 'resolve' ? StatusPalette.green :
    p.$variant === 'ignore' ? StatusPalette.amber :
    p.$variant === 'details' ? p.theme.colors.tint :
    p.theme.colors.text};
  font-size: 12px;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.15s ease;
  text-decoration: none;
  display: inline-block;

  &:hover {
    background-color: ${p =>
      p.$variant === 'resolve' ? `${StatusPalette.green}30` :
      p.$variant === 'ignore' ? `${StatusPalette.amber}30` :
      p.$variant === 'details' ? (p.theme.colors.tint + '30') :
      p.theme.colors.border};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ActionsWrapper = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
`;

const MenuWrapper = styled.div`
  position: relative;
  display: inline-block;
`;

const MenuTrigger = styled.button`
  padding: 3px 6px;
  border-radius: ${p => p.theme.borderRadius.sm || '4px'};
  border: 1px solid ${p => p.theme.colors.border};
  background-color: transparent;
  color: ${p => p.theme.colors.muted};
  font-size: 14px;
  cursor: pointer;
  line-height: 1;
  transition: all 0.15s ease;

  &:hover {
    background-color: ${p => p.theme.colors.border};
    color: ${p => p.theme.colors.text};
  }
`;

const MenuDropdown = styled.div`
  position: absolute;
  right: 0;
  top: 100%;
  margin-top: 4px;
  min-width: 130px;
  background-color: ${p => p.theme.colors.backgroundSecondary};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 50;
  overflow: hidden;
`;

const MenuItem = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 12px;
  border: none;
  background: none;
  color: ${p =>
    p.$variant === 'resolve' ? StatusPalette.green :
    p.$variant === 'ignore' ? StatusPalette.amber :
    p.theme.colors.text};
  font-size: 13px;
  cursor: pointer;
  text-align: left;

  &:hover {
    background-color: ${p => p.theme.colors.background};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ProjectBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  background-color: ${p =>
    p.$project === 'aqrate-api' ? `${StatusPalette.blue}20` :
    p.$project === 'aqrate-mobile' ? `${StatusPalette.purple}20` :
    p.$project === 'aqrate-portal' ? `${StatusPalette.green}20` :
    p.$project === 'aqrate-pdf-service' ? `${StatusPalette.orange}20` :
    `${StatusPalette.slate}20`};
  color: ${p =>
    p.$project === 'aqrate-api' ? StatusPalette.blue :
    p.$project === 'aqrate-mobile' ? StatusPalette.purple :
    p.$project === 'aqrate-portal' ? StatusPalette.green :
    p.$project === 'aqrate-pdf-service' ? StatusPalette.orange :
    StatusPalette.slate};
`;

const LevelBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  ${p => {
    const c = SentryLevelColors[p.$level] || StatusPalette.blue;
    return `background-color: ${withOpacity(c)}; color: ${c};`;
  }}
`;

const SentryLink = styled.a`
  color: ${p => p.theme.colors.tint};
  font-size: 12px;
  text-decoration: none;
  white-space: nowrap;

  &:hover {
    text-decoration: underline;
  }
`;

const SentryNavLink = styled(Link)`
  color: ${p => p.theme.colors.tint};
  font-size: 12px;
  text-decoration: none;
  white-space: nowrap;
  font-weight: 600;

  &:hover {
    text-decoration: underline;
  }
`;

const WarningBanner = styled.div`
  padding: ${p => p.theme.spacing.sm} ${p => p.theme.spacing.md};
  background-color: ${StatusPalette.amber}15;
  border: 1px solid ${StatusPalette.amber}40;
  border-radius: ${p => p.theme.borderRadius.md};
  color: ${StatusPalette.amber};
  font-size: 13px;
  margin-bottom: ${p => p.theme.spacing.md};
`;

// --- Tab Components ---

const ConnectivityTab = ({ data, loading, onRefresh }) => {
  const theme = useTheme();

  return (
    <>
      <div style={{ marginBottom: theme.spacing.lg }}>
        <Button variant="secondary" size="sm" onClick={onRefresh} disabled={loading}>
          {loading ? 'Testing...' : 'Test All'}
        </Button>
      </div>
      {data?.results ? (
        <CardGrid>
          {data.results.map((r, i) => (
            <Card key={i}>
              <CardHeader>
                <CardTitle>
                  <StatusDot $status={r.status} />
                  <ThemedText type="defaultSemiBold" style={{ color: theme.colors.text }}>
                    {r.service}
                  </ThemedText>
                </CardTitle>
                <ThemedText type="small" style={{ color: theme.colors.muted }}>
                  {r.responseTimeMs}ms
                </ThemedText>
              </CardHeader>
              <CardBody>
                <InfoRow>
                  <InfoLabel>Status</InfoLabel>
                  <StatusBadge $variant={r.status}>{r.status.toUpperCase()}</StatusBadge>
                </InfoRow>
                <InfoRow>
                  <InfoLabel>Response Time</InfoLabel>
                  <InfoValue>{r.responseTimeMs}ms</InfoValue>
                </InfoRow>
                {r.error && (
                  <InfoRow>
                    <InfoLabel>Error</InfoLabel>
                    <InfoValue style={{ color: StatusPalette.red }}>{r.error}</InfoValue>
                  </InfoRow>
                )}
              </CardBody>
            </Card>
          ))}
        </CardGrid>
      ) : !loading ? (
        <EmptyState>Click "Test All" to check connectivity</EmptyState>
      ) : null}
      {data?.checkedAt && (
        <LastChecked>Last checked: {new Date(data.checkedAt).toLocaleTimeString()}</LastChecked>
      )}
    </>
  );
};

const BackgroundJobsTab = ({ data, loading }) => {
  const theme = useTheme();

  if (!data?.jobs) return loading ? null : <EmptyState>No job data available</EmptyState>;

  const jobs = [...data.jobs].sort((a, b) => a.serviceName.localeCompare(b.serviceName));

  return (
    <CardGrid>
      {jobs.map(job => (
        <Card key={job.serviceName}>
          <CardHeader>
            <CardTitle>
              <StatusDot $status={job.lastRunSuccess === true ? 'ok' : job.lastRunSuccess === false ? 'error' : 'loading'} />
              <ThemedText type="defaultSemiBold" style={{ color: theme.colors.text }}>
                {job.serviceName}
              </ThemedText>
            </CardTitle>
            <ThemedText type="small" style={{ color: theme.colors.muted }}>
              {job.runCount} runs
            </ThemedText>
          </CardHeader>
          <CardBody>
            <InfoRow>
              <InfoLabel>Last Run</InfoLabel>
              <InfoValue>{job.lastRunAt ? formatTimeAgo(job.lastRunAt) : 'Not yet'}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Last Result</InfoLabel>
              <InfoValue>
                {job.lastRunSuccess === true ? 'Success' : job.lastRunSuccess === false ? 'Failed' : 'Pending'}
              </InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Message</InfoLabel>
              <InfoValue>{job.lastMessage || '-'}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Next Run</InfoLabel>
              <InfoValue>{job.nextRunAt ? formatDateTime(job.nextRunAt) : '-'}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Error Count</InfoLabel>
              <InfoValue style={{ color: job.errorCount > 0 ? StatusPalette.red : undefined }}>
                {job.errorCount}
              </InfoValue>
            </InfoRow>
            {job.lastError && (
              <InfoRow>
                <InfoLabel>Last Error</InfoLabel>
                <InfoValue style={{ color: StatusPalette.red }}>{job.lastError}</InfoValue>
              </InfoRow>
            )}
          </CardBody>
        </Card>
      ))}
      {data?.queriedAt && (
        <LastChecked>Queried: {new Date(data.queriedAt).toLocaleTimeString()}</LastChecked>
      )}
    </CardGrid>
  );
};

const PdfQueueTab = ({ data, loading, onRefresh }) => {
  const [retriggeringId, setRetriggeringId] = useState(null);

  const handleRetrigger = async (reportId) => {
    setRetriggeringId(reportId);
    try {
      await pdfReportService.regenerateReport(reportId);
      toast.success('Report re-triggered');
      onRefresh();
    } catch {
      toast.error('Failed to re-trigger report');
    } finally {
      setRetriggeringId(null);
    }
  };

  if (!data) return loading ? null : <EmptyState>No queue data available</EmptyState>;

  return (
    <>
      <SummaryBar>
        <SummaryItem>
          <SummaryCount $color={ReportStatusColors.Pending}>{data.summary?.pending ?? 0}</SummaryCount> Pending
        </SummaryItem>
        <SummaryItem>
          <SummaryCount $color={ReportStatusColors.Processing}>{data.summary?.processing ?? 0}</SummaryCount> Processing
        </SummaryItem>
        <SummaryItem>
          <SummaryCount $color={ReportStatusColors.Failed}>{data.summary?.failed ?? 0}</SummaryCount> Failed
        </SummaryItem>
      </SummaryBar>

      {data.reports?.length > 0 ? (
        <TableContainer>
          <Table>
            <Thead>
              <tr>
                <Th>ID</Th>
                <Th>Form</Th>
                <Th>Submission</Th>
                <Th>Status</Th>
                <Th>Retries</Th>
                <Th>Created</Th>
                <Th>Waiting</Th>
                <Th>Error</Th>
                <Th>Actions</Th>
              </tr>
            </Thead>
            <tbody>
              {data.reports.map(r => (
                <tr key={r.id}>
                  <Td>{r.id}</Td>
                  <Td><TruncatedText>{r.formSchemaName}</TruncatedText></Td>
                  <Td>{r.formSubmissionId}</Td>
                  <Td><StatusBadge $variant={r.status}>{r.status}</StatusBadge></Td>
                  <Td>{r.retryCount}/{r.maxRetries}</Td>
                  <Td>{formatTimeAgo(r.createdAt)}</Td>
                  <Td>{r.waitingSeconds ? `${Math.floor(r.waitingSeconds)}s` : '-'}</Td>
                  <Td><TruncatedText title={r.errorMessage}>{r.errorMessage || '-'}</TruncatedText></Td>
                  <Td>
                    {r.status === 'Failed' && (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handleRetrigger(r.id)}
                        disabled={retriggeringId === r.id}
                      >
                        {retriggeringId === r.id ? '...' : 'Retry'}
                      </Button>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableContainer>
      ) : (
        <EmptyState>No pending, processing, or failed reports</EmptyState>
      )}
    </>
  );
};

const ConfigurationTab = ({ data, loading }) => {
  if (!data?.groups) return loading ? null : <EmptyState>No configuration data available</EmptyState>;

  return (
    <CardGrid>
      {Object.entries(data.groups).map(([category, values]) => (
        <Card key={category}>
          <CardHeader>
            <CardTitle>
              <ThemedText type="defaultSemiBold">{category}</ThemedText>
            </CardTitle>
          </CardHeader>
          <CardBody>
            {Object.entries(values).map(([key, value]) => (
              <InfoRow key={key}>
                <InfoLabel>{key}</InfoLabel>
                <InfoValue>{value || <span style={{ opacity: 0.4 }}>not set</span>}</InfoValue>
              </InfoRow>
            ))}
          </CardBody>
        </Card>
      ))}
    </CardGrid>
  );
};

const RecentErrorsTab = ({ data, loading, onRefresh, onClear }) => {
  const theme = useTheme();

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: theme.spacing.lg, flexWrap: 'wrap', gap: theme.spacing.sm }}>
        <ThemedText type="default" style={{ color: theme.colors.muted }}>
          Total captured: <strong style={{ color: theme.colors.text }}>{data?.totalCaptured ?? 0}</strong>
          {data?.errors?.length > 0 && ` (showing ${data.errors.length})`}
        </ThemedText>
        <HeaderActions>
          <Button variant="secondary" size="sm" onClick={onRefresh} disabled={loading}>
            Refresh
          </Button>
          <Button variant="secondary" size="sm" onClick={onClear} disabled={loading || !data?.errors?.length}>
            Clear All
          </Button>
        </HeaderActions>
      </div>

      {data?.errors?.length > 0 ? (
        <TableContainer>
          <Table>
            <Thead>
              <tr>
                <Th>Time</Th>
                <Th>Type</Th>
                <Th>Message</Th>
                <Th>Path</Th>
                <Th>Method</Th>
                <Th>Status</Th>
                <Th>Sentry ID</Th>
                <Th>User</Th>
              </tr>
            </Thead>
            <tbody>
              {data.errors.map((err, i) => (
                <tr key={i}>
                  <Td style={{ whiteSpace: 'nowrap' }}>{formatTimeAgo(err.timestamp)}</Td>
                  <Td><StatusBadge $variant="Failed">{err.exceptionType}</StatusBadge></Td>
                  <Td><TruncatedText title={err.message}>{err.message}</TruncatedText></Td>
                  <Td><TruncatedText>{err.requestPath || '-'}</TruncatedText></Td>
                  <Td>{err.httpMethod || '-'}</Td>
                  <Td>{err.statusCode}</Td>
                  <Td><TruncatedText>{err.sentryEventId || '-'}</TruncatedText></Td>
                  <Td>{err.userId || '-'}</Td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableContainer>
      ) : (
        <EmptyState>No errors captured yet</EmptyState>
      )}
    </>
  );
};

const SentryIssuesTab = ({ data, loading, onRefresh, onFilterChange, filters }) => {
  const theme = useTheme();
  const allIssues = data?.issues || [];
  const [updatingIssue, setUpdatingIssue] = useState(null);
  const [openMenu, setOpenMenu] = useState(null);
  const menuRef = useRef(null);

  // Close menu on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpenMenu(null);
      }
    };
    if (openMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [openMenu]);

  const filteredIssues = useMemo(() => {
    if (!filters.searchText) return allIssues;
    const search = filters.searchText.toLowerCase();
    return allIssues.filter(issue =>
      (issue.title && issue.title.toLowerCase().includes(search)) ||
      (issue.culprit && issue.culprit.toLowerCase().includes(search)) ||
      (issue.shortId && issue.shortId.toLowerCase().includes(search))
    );
  }, [allIssues, filters.searchText]);

  const handleUpdateStatus = async (issueId, status) => {
    setOpenMenu(null);
    setUpdatingIssue(issueId + '-' + status);
    try {
      await systemStatusService.updateSentryIssueStatus(issueId, status);
      toast.success(`Issue ${status === 'resolved' ? 'resolved' : status === 'ignored' ? 'ignored' : 'unresolved'}`);
      onRefresh();
    } catch {
      toast.error('Failed to update issue status');
    } finally {
      setUpdatingIssue(null);
    }
  };

  return (
    <>
      <FilterBar>
        <FilterSelect
          value={filters.project || ''}
          onChange={e => onFilterChange({ ...filters, project: e.target.value || null })}
        >
          <option value="">All Projects</option>
          <option value="aqrate-api">aqrate-api</option>
          <option value="aqrate-mobile">aqrate-mobile</option>
          <option value="aqrate-portal">aqrate-portal</option>
          <option value="aqrate-pdf-service">aqrate-pdf-service</option>
        </FilterSelect>
        <FilterSelect
          value={filters.query || 'is:unresolved'}
          onChange={e => onFilterChange({ ...filters, query: e.target.value })}
        >
          <option value="is:unresolved">Unresolved</option>
          <option value="is:resolved">Resolved</option>
          <option value="is:ignored">Ignored</option>
          <option value="">All</option>
        </FilterSelect>
        <FilterSelect
          value={filters.statsPeriod || '14d'}
          onChange={e => onFilterChange({ ...filters, statsPeriod: e.target.value })}
        >
          <option value="1h">Last 1 hour</option>
          <option value="24h">Last 24 hours</option>
          <option value="48h">Last 48 hours</option>
          <option value="7d">Last 7 days</option>
          <option value="14d">Last 14 days</option>
          <option value="30d">Last 30 days</option>
          <option value="90d">Last 90 days</option>
        </FilterSelect>
        <FilterSelect
          value={filters.sort || 'date'}
          onChange={e => onFilterChange({ ...filters, sort: e.target.value })}
        >
          <option value="date">Last Seen</option>
          <option value="new">First Seen</option>
          <option value="freq">Events</option>
          <option value="user">Users</option>
        </FilterSelect>
        <SearchInput
          type="text"
          placeholder="Search issues..."
          value={filters.searchText || ''}
          onChange={e => onFilterChange({ ...filters, searchText: e.target.value })}
        />
        <Button variant="secondary" size="sm" onClick={onRefresh} disabled={loading}>
          {loading ? 'Loading...' : 'Refresh'}
        </Button>
        <span style={{ color: theme.colors.muted, fontSize: 13, marginLeft: 'auto' }}>
          {filteredIssues.length}{filteredIssues.length !== allIssues.length ? `/${allIssues.length}` : ''} issue{filteredIssues.length !== 1 ? 's' : ''}
        </span>
      </FilterBar>

      {data?.errors && data.errors.length > 0 && (
        <WarningBanner>
          Partial failure: {data.errors.join('; ')}
        </WarningBanner>
      )}

      {data?.error && (
        <EmptyState>{data.error}</EmptyState>
      )}

      {!data?.error && filteredIssues.length > 0 ? (
        <TableContainer>
          <Table>
            <Thead>
              <tr>
                <Th>Project</Th>
                <Th>Level</Th>
                <Th>Issue</Th>
                <Th>Events</Th>
                <Th>Users</Th>
                <Th>First Seen</Th>
                <Th>Last Seen</Th>
                <Th>Actions</Th>
              </tr>
            </Thead>
            <tbody>
              {filteredIssues.map(issue => (
                <tr key={issue.id}>
                  <Td><ProjectBadge $project={issue.projectSlug}>{issue.projectName}</ProjectBadge></Td>
                  <Td><LevelBadge $level={issue.level}>{issue.level}</LevelBadge></Td>
                  <Td>
                    <div style={{ fontSize: 11, color: theme.colors.muted }}>{issue.shortId}</div>
                    <TruncatedText title={issue.title} style={{ fontWeight: 500 }}>{issue.title}</TruncatedText>
                    {issue.culprit && (
                      <div style={{ fontSize: 11, color: theme.colors.muted }}>{issue.culprit}</div>
                    )}
                  </Td>
                  <Td>{issue.count}</Td>
                  <Td>{issue.userCount}</Td>
                  <Td style={{ whiteSpace: 'nowrap' }}>{formatTimeAgo(issue.firstSeen)}</Td>
                  <Td style={{ whiteSpace: 'nowrap' }}>{formatTimeAgo(issue.lastSeen)}</Td>
                  <Td>
                    <ActionsWrapper>
                      <ActionButton
                        as="a"
                        $variant="details"
                        href={`/sentry-issues/${issue.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Open details in new tab"
                      >
                        Details
                      </ActionButton>
                      {issue.permalink && (
                        <ActionButton
                          as="a"
                          href={issue.permalink}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="Open in Sentry"
                        >
                          <ExternalLink size={14} />
                        </ActionButton>
                      )}
                      <MenuWrapper ref={openMenu === issue.id ? menuRef : null}>
                        <MenuTrigger
                          onClick={() => setOpenMenu(openMenu === issue.id ? null : issue.id)}
                          title="More actions"
                        >
                          <MoreHorizontal size={14} />
                        </MenuTrigger>
                        {openMenu === issue.id && (
                          <MenuDropdown>
                            {issue.status !== 'resolved' && (
                              <MenuItem
                                $variant="resolve"
                                onClick={() => handleUpdateStatus(issue.id, 'resolved')}
                                disabled={updatingIssue === issue.id + '-resolved'}
                              >
                                {updatingIssue === issue.id + '-resolved' ? '...' : 'Resolve'}
                              </MenuItem>
                            )}
                            {issue.status !== 'ignored' && (
                              <MenuItem
                                $variant="ignore"
                                onClick={() => handleUpdateStatus(issue.id, 'ignored')}
                                disabled={updatingIssue === issue.id + '-ignored'}
                              >
                                {updatingIssue === issue.id + '-ignored' ? '...' : 'Ignore'}
                              </MenuItem>
                            )}
                          </MenuDropdown>
                        )}
                      </MenuWrapper>
                    </ActionsWrapper>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableContainer>
      ) : !data?.error && !loading ? (
        <EmptyState>No issues found</EmptyState>
      ) : null}

      {data?.queriedAt && (
        <LastChecked>Queried: {new Date(data.queriedAt).toLocaleTimeString()}</LastChecked>
      )}
    </>
  );
};

// --- AI Models Tab ---

const AIModelCard = styled.div`
  background: ${p => p.theme.colors.backgroundSecondary};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: 8px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const AIModelGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
`;

const AIModelCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const AIModelCardTitle = styled.h4`
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: ${p => p.theme.colors.text};
`;


const AIModelActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 4px;
`;

const PricingRow = styled.div`
  display: flex;
  gap: 8px;
`;

const PriceChip = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  background: ${p => p.theme.colors.background};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: 6px;
  padding: 6px 8px;
`;

const PriceChipLabel = styled.span`
  font-size: 10px;
  color: ${p => p.theme.colors.muted};
  text-transform: uppercase;
  letter-spacing: 0.4px;
`;

const PriceChipValue = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${p => p.theme.colors.text};
`;

const PriceChipUnit = styled.span`
  font-size: 9px;
  color: ${p => p.theme.colors.muted};
`;

const SmallButton = styled.button`
  padding: 4px 10px;
  font-size: 11px;
  border-radius: 4px;
  border: 1px solid ${p => p.theme.colors.border};
  background: ${p => p.variant === 'primary' ? p.theme.colors.primary : p.theme.colors.backgroundSecondary};
  color: ${p => p.variant === 'primary' ? '#fff' : p.theme.colors.text};
  cursor: pointer;
  &:hover { opacity: 0.8; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const EditModalTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: ${p => p.theme.colors.text};
`;

const EditField = styled.div``;

const QuickPickRow = styled.div`
  display: flex;
  gap: 6px;
  margin-top: 6px;
`;

const QuickPickBtn = styled.button`
  flex: 1;
  padding: 6px 8px;
  font-size: 11px;
  font-weight: 600;
  border-radius: 6px;
  border: 1px solid ${p => p.$active ? p.theme.colors.primary : p.theme.colors.border};
  background: ${p => p.$active ? p.theme.colors.primary + '14' : p.theme.colors.backgroundSecondary};
  color: ${p => p.$active ? p.theme.colors.primary : p.theme.colors.textSecondary};
  cursor: pointer;
  transition: all 0.15s;
  &:hover { border-color: ${p => p.theme.colors.primary}; color: ${p => p.theme.colors.primary}; }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const EditLabel = styled.label`
  display: block;
  font-size: 12px;
  font-weight: 500;
  color: ${p => p.theme.colors.muted};
  margin-bottom: 4px;
`;

const EditInput = styled.input`
  width: 100%;
  padding: 8px 10px;
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: 6px;
  background: ${p => p.theme.colors.background};
  color: ${p => p.theme.colors.text};
  font-size: 13px;
  box-sizing: border-box;
`;

const ActiveBadge = styled.span`
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
  background: ${p => p.$active ? '#dcfce7' : '#fef2f2'};
  color: ${p => p.$active ? '#166534' : '#991b1b'};
`;

const ProviderBadge = styled.span`
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
  background: ${p => p.$provider === 'OpenRouter' ? '#ede9fe' : '#e0e7ff'};
  color: ${p => p.$provider === 'OpenRouter' ? '#6d28d9' : '#3730a3'};
`;

// Marks purposes that drive the tool/structured APIs (AIToolCallingPurposes.All on the API) — their
// model must support function calling, and the picker filters OpenRouter to tools-capable models.
const ToolBadge = styled.span`
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
  background: #ccfbf1;
  color: #0f766e;
`;

const UsageSummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: ${p => p.theme.spacing.md};
  margin-bottom: ${p => p.theme.spacing.lg};
`;

const UsageStatCard = styled.div`
  background: ${p => p.theme.colors.backgroundSecondary};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  padding: ${p => p.theme.spacing.md};
`;

const UsageStatLabel = styled.div`
  font-size: 11px;
  color: ${p => p.theme.colors.muted};
  margin-bottom: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const UsageStatValue = styled.div`
  font-size: 22px;
  font-weight: 600;
  color: ${p => p.theme.colors.text};
`;

const FilterRow = styled.div`
  display: flex;
  gap: ${p => p.theme.spacing.sm};
  margin-bottom: ${p => p.theme.spacing.md};
  flex-wrap: wrap;
  align-items: center;
`;

const FilterInput = styled.input`
  padding: 6px 10px;
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.sm};
  background: ${p => p.theme.colors.background};
  color: ${p => p.theme.colors.text};
  font-size: 12px;
`;

const PaginationRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: ${p => p.theme.spacing.md};
  font-size: 12px;
  color: ${p => p.theme.colors.muted};
`;

const PaginationButton = styled.button`
  padding: 4px 10px;
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.sm};
  background: ${p => p.disabled ? p.theme.colors.backgroundSecondary : p.theme.colors.background};
  color: ${p => p.disabled ? p.theme.colors.muted : p.theme.colors.text};
  cursor: ${p => p.disabled ? 'default' : 'pointer'};
  font-size: 12px;
  &:hover:not(:disabled) { background: ${p => p.theme.colors.backgroundSecondary}; }
`;

const UsageStatusBadge = styled.span`
  padding: 2px 6px;
  border-radius: 8px;
  font-size: 10px;
  font-weight: 500;
  background: ${p => p.$status === 'Success' ? '#dcfce7' : '#fef2f2'};
  color: ${p => p.$status === 'Success' ? '#166534' : '#991b1b'};
`;

const AIUsageBanner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: ${p => p.theme.colors.tint}12;
  border: 1px solid ${p => p.theme.colors.tint}30;
  border-radius: ${p => p.theme.borderRadius.md};
  margin-bottom: ${p => p.theme.spacing.lg};
`;

// Purposes whose model is fixed (non-LLM) — skip the model picker, show a static row.
const NON_LLM_PURPOSES = new Set(['Textract', 'ImageGeneration', 'ImageGenerationMini']);
// Purposes that always run through Bedrock's tool-calling path — OpenRouter would 400.
// ponytail: keep in sync with aQRate.Domain/Constants/AIToolCallingPurposes.cs
const TOOL_CALLING_PURPOSES = new Set(['FormVisualizationProject', 'DemoDataGenerator', 'DemoScaffoldPlanner', 'ImageGenPlan']);

// Best-effort vendor from a modelId, matching the API's own convention (prefix before '/').
const deriveVendor = (modelId, provider) => {
  if (!modelId) return provider || '';
  if (modelId.includes('/')) return modelId.split('/')[0];          // OpenRouter: z-ai/glm-5.2
  const id = modelId.toLowerCase();
  if (id.startsWith('gpt') || id.includes('openai')) return 'openai';
  if (id.includes('anthropic') || id.includes('claude')) return 'anthropic';
  if (id.includes('textract') || id.startsWith('aws')) return 'aws';
  const seg = modelId.split(/[.:]/);                                 // us.anthropic.claude-… → anthropic
  return seg.length > 1 ? seg[1] : modelId;
};

const tokensShort = (v) =>
  !v ? '0' : v >= 1048576 ? `${v / 1048576}M` : v >= 1024 ? `${Math.round(v / 1024)}k` : `${v}`;

const cardMeta = (c) => {
  if (c.purpose === 'Textract') return `${formatTokenPrice(c.costPerPage)} / page`;
  const parts = [];
  if (c.maxTokens) parts.push(`${tokensShort(c.maxTokens)} tok`);
  parts.push(`temp ${c.temperature}`);
  if (c.inputPricePerMillionTokens || c.outputPricePerMillionTokens) {
    parts.push(`${formatTokenPrice(c.inputPricePerMillionTokens)} / ${formatTokenPrice(c.outputPricePerMillionTokens)}`);
  }
  return parts.join('  ·  ');
};

const CardHeadLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

const CardModelLine = styled.div`
  font-family: monospace;
  font-size: 12px;
  color: ${p => p.theme.colors.text};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CardMeta = styled.div`
  font-size: 12px;
  color: ${p => p.theme.colors.muted};
`;

const ModelToolbar = styled.div`
  display: flex;
  align-items: center;
  gap: ${p => p.theme.spacing.sm};
  margin-bottom: ${p => p.theme.spacing.md};
`;

const ModelSearchBox = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 1;
  max-width: 340px;
  padding: 7px 10px;
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: 8px;
  background: ${p => p.theme.colors.backgroundSecondary};
  color: ${p => p.theme.colors.muted};
  input {
    flex: 1;
    border: none;
    outline: none;
    background: transparent;
    color: ${p => p.theme.colors.text};
    font-size: 13px;
  }
`;

const OverrideToggle = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 10px;
  padding: 0;
  background: none;
  border: none;
  color: ${p => p.theme.colors.muted};
  font-size: 12px;
  cursor: pointer;
  &:hover { color: ${p => p.theme.colors.primary}; }
`;

const AutoNote = styled.span`
  font-size: 11px;
  font-weight: 400;
  color: ${p => p.theme.colors.muted};
  margin-left: 6px;
`;

const StaticModelRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: 8px;
  background: ${p => p.theme.colors.background};
  font-family: monospace;
  font-size: 13px;
  color: ${p => p.theme.colors.text};
`;

const AIModelsTab = ({
  configs, availableModels, loading, onRefresh,
  openRouterModels, openRouterModelsLoading, onFetchOpenRouterModels,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [editingConfig, setEditingConfig] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  const [showOverride, setShowOverride] = useState(false);
  const [cardQuery, setCardQuery] = useState('');

  const handleEdit = (config) => {
    setEditingConfig(config);
    setShowOverride(false);
    setEditForm({
      modelId: config.modelId,
      provider: config.provider || 'Bedrock',
      maxTokens: config.maxTokens,
      temperature: config.temperature,
      isActive: config.isActive,
      inputPricePerMillionTokens: config.inputPricePerMillionTokens ?? 0,
      outputPricePerMillionTokens: config.outputPricePerMillionTokens ?? 0,
      costPerPage: config.costPerPage ?? 0,
    });
  };

  const handleProviderChange = (provider) => {
    setEditForm(f => ({ ...f, provider }));
    if (provider === 'OpenRouter') onFetchOpenRouterModels();
  };

  // Picking a model in the ModelPicker: set the id and auto-fill prices (only when the
  // catalog actually knows a price, so an unknown Bedrock model keeps the existing values).
  const handlePickModel = (model) => {
    setEditForm(f => ({
      ...f,
      modelId: model.modelId,
      ...(model.inputPricePerMillionTokens > 0 || model.outputPricePerMillionTokens > 0
        ? {
            inputPricePerMillionTokens: model.inputPricePerMillionTokens,
            outputPricePerMillionTokens: model.outputPricePerMillionTokens,
          }
        : {}),
    }));
  };

  const handleSave = async () => {
    if (!editingConfig) return;
    setSaving(true);
    try {
      await aiModelService.updateConfig(editingConfig.id, editForm);
      toast.success(`Updated ${editingConfig.displayName}`);
      setEditingConfig(null);
      onRefresh();
    } catch (err) {
      // Surface the API's specific reason (e.g. "'X' doesn't support tools — pick a function-calling model")
      // instead of a generic failure. apiRequest throws an ApiError whose message is already the extracted
      // backend error (getErrorMessage prefers response data.error); data holds the raw { error } body.
      toast.error(err?.data?.error || err?.message || 'Failed to update config');
    } finally {
      setSaving(false);
    }
  };

  const findLatest = (keyword) => {
    if (!availableModels?.length) return null;
    // Find the latest model matching the keyword (last in the sorted list = newest)
    const matches = availableModels.filter(m =>
      m.modelId.toLowerCase().includes(keyword.toLowerCase())
    );
    return matches.length > 0 ? matches[matches.length - 1] : null;
  };

  const handleTest = async (config) => {
    setTesting(config.id);
    try {
      const result = await aiModelService.testModel(config.modelId, config.provider || 'Bedrock');
      if (result.success) {
        toast.success(`${config.displayName}: OK (${result.latencyMs}ms, ${result.inputTokens}+${result.outputTokens} tokens)`);
      } else {
        toast.error(`${config.displayName}: ${result.error}`);
      }
    } catch {
      toast.error('Test failed');
    } finally {
      setTesting(null);
    }
  };

  const q = cardQuery.trim().toLowerCase();
  const shownConfigs = !q
    ? (configs || [])
    : (configs || []).filter(c =>
        [c.displayName, c.purpose, c.modelId, c.provider].some(x => (x || '').toLowerCase().includes(q)));

  // Model picker inputs for the open editor.
  const isNonLlm = editingConfig && NON_LLM_PURPOSES.has(editingConfig.purpose);
  const requiresTools = !!editingConfig && TOOL_CALLING_PURPOSES.has(editingConfig.purpose);
  const rawPickerModels = editForm.provider === 'OpenRouter' ? (openRouterModels || []) : (availableModels || []);
  // Tool-calling purposes need a function-calling model. Bedrock's Anthropic models all qualify; on OpenRouter
  // we filter the catalog to models whose supported_parameters advertise tools (backend also enforces on save).
  const pickerModels = requiresTools && editForm.provider === 'OpenRouter'
    ? rawPickerModels.filter(m => m.supportsTools)
    : rawPickerModels;
  const pickerLoading = editForm.provider === 'OpenRouter' ? openRouterModelsLoading : false;
  const suggestions = editForm.provider === 'Bedrock'
    ? ['haiku', 'sonnet', 'opus'].map(k => { const m = findLatest(k); return m ? { label: k, model: m } : null; }).filter(Boolean)
    : null;

  if (loading) return <EmptyState>Loading AI model data...</EmptyState>;

  return (
    <>
      {/* AI Usage banner */}
      <AIUsageBanner theme={theme}>
        <span style={{ fontSize: 13, color: theme.colors.text }}>
          View detailed AI usage logs, token consumption, and estimated costs on the dedicated AI Usage page.
        </span>
        <SmallButton variant="primary" onClick={() => navigate('/ai-usage')}>
          View AI Usage →
        </SmallButton>
      </AIUsageBanner>

      {/* Model Assignments */}
      <SectionTitle style={{ color: theme.colors.text }}>Model Assignments</SectionTitle>
      <ModelToolbar>
        <ModelSearchBox>
          <Search size={15} />
          <input
            placeholder="Search assignments by purpose, model, or provider…"
            value={cardQuery}
            onChange={e => setCardQuery(e.target.value)}
          />
        </ModelSearchBox>
      </ModelToolbar>
      <AIModelGrid>
        {shownConfigs.map(c => (
          <AIModelCard key={c.id}>
            <AIModelCardHeader>
              <CardHeadLeft>
                <VendorIcon vendor={deriveVendor(c.modelId, c.provider)} size={26} />
                <AIModelCardTitle title={c.displayName}>{c.displayName}</AIModelCardTitle>
              </CardHeadLeft>
              <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                {TOOL_CALLING_PURPOSES.has(c.purpose) && (
                  <ToolBadge title="Requires a function-calling model">Function-calling</ToolBadge>
                )}
                <ProviderBadge $provider={c.provider}>{c.provider || 'Bedrock'}</ProviderBadge>
                {!c.isActive && <ActiveBadge $active={false}>Inactive</ActiveBadge>}
              </div>
            </AIModelCardHeader>
            <CardModelLine title={c.modelId}>{c.modelId}</CardModelLine>
            <CardMeta>{cardMeta(c)}</CardMeta>
            <AIModelActions>
              <SmallButton onClick={() => handleEdit(c)}>Edit</SmallButton>
              <SmallButton
                variant="primary"
                onClick={() => handleTest(c)}
                disabled={testing === c.id}
              >
                {testing === c.id ? 'Testing...' : 'Test'}
              </SmallButton>
            </AIModelActions>
          </AIModelCard>
        ))}
        {shownConfigs.length === 0 && <EmptyState>No assignments match “{cardQuery}”.</EmptyState>}
      </AIModelGrid>

      <SectionTitle style={{ color: theme.colors.text }}>
        Available Inference Profiles
        <SmallButton onClick={onRefresh} style={{ marginLeft: 12, fontSize: 11 }}>Refresh</SmallButton>
        {availableModels?.some(m => !m.isActive) && (
          <SmallButton
            onClick={() => setShowInactive(v => !v)}
            style={{ marginLeft: 6, fontSize: 11 }}
          >
            {showInactive ? 'Hide inactive' : 'Show inactive'}
          </SmallButton>
        )}
      </SectionTitle>
      {availableModels?.filter(m => showInactive || m.isActive).length > 0 ? (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${theme.colors.border}`, textAlign: 'left' }}>
                <th style={{ padding: '8px 12px', color: theme.colors.textSecondary }}>Inference Profile ID</th>
                <th style={{ padding: '8px 12px', color: theme.colors.textSecondary }}>Name</th>
                <th style={{ padding: '8px 12px', color: theme.colors.textSecondary }}>Input $/1M</th>
                <th style={{ padding: '8px 12px', color: theme.colors.textSecondary }}>Output $/1M</th>
                <th style={{ padding: '8px 12px', color: theme.colors.textSecondary }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {availableModels.filter(m => showInactive || m.isActive).map(m => (
                <tr key={m.modelId} style={{ borderBottom: `1px solid ${theme.colors.border}` }}>
                  <td style={{ padding: '8px 12px', fontFamily: 'monospace', color: theme.colors.text }}>{m.modelId}</td>
                  <td style={{ padding: '8px 12px', color: theme.colors.text }}>{m.modelName}</td>
                  <td style={{ padding: '8px 12px', color: theme.colors.textSecondary }}>{m.inputPricePerMillionTokens > 0 ? `$${m.inputPricePerMillionTokens.toFixed(2)}` : '—'}</td>
                  <td style={{ padding: '8px 12px', color: theme.colors.textSecondary }}>{m.outputPricePerMillionTokens > 0 ? `$${m.outputPricePerMillionTokens.toFixed(2)}` : '—'}</td>
                  <td style={{ padding: '8px 12px' }}>
                    <ActiveBadge $active={m.isActive}>{m.isActive ? 'Active' : 'Inactive'}</ActiveBadge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <EmptyState>No inference profiles available. Check Bedrock region and credentials.</EmptyState>
      )}

      {editingConfig && (
        <ModalOverlay isOpen onClick={() => setEditingConfig(null)}>
          <ModalContent style={{ maxWidth: 500 }} onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <EditModalTitle>Edit: {editingConfig.displayName}</EditModalTitle>
            </ModalHeader>
            <ModalBody>
              {isNonLlm ? (
                <>
                  <EditField>
                    <EditLabel>Model</EditLabel>
                    <StaticModelRow>
                      <VendorIcon vendor={deriveVendor(editForm.modelId, editForm.provider)} size={22} />
                      <span>{editForm.modelId}</span>
                    </StaticModelRow>
                    <AutoNote>This purpose uses a fixed model.</AutoNote>
                  </EditField>
                  {editingConfig.purpose === 'Textract' ? (
                    <EditField>
                      <EditLabel>Cost Per Page ($)</EditLabel>
                      <EditInput
                        type="number" step="0.001" min="0"
                        value={editForm.costPerPage ?? 0}
                        onChange={e => setEditForm(f => ({ ...f, costPerPage: parseFloat(e.target.value) || 0 }))}
                      />
                    </EditField>
                  ) : (
                    <>
                      <EditField>
                        <EditLabel>Input Price ($/1M tokens)</EditLabel>
                        <EditInput
                          type="number" step="0.01" min="0"
                          value={editForm.inputPricePerMillionTokens ?? 0}
                          onChange={e => setEditForm(f => ({ ...f, inputPricePerMillionTokens: parseFloat(e.target.value) || 0 }))}
                        />
                      </EditField>
                      <EditField>
                        <EditLabel>Output Price ($/1M tokens)</EditLabel>
                        <EditInput
                          type="number" step="0.01" min="0"
                          value={editForm.outputPricePerMillionTokens ?? 0}
                          onChange={e => setEditForm(f => ({ ...f, outputPricePerMillionTokens: parseFloat(e.target.value) || 0 }))}
                        />
                      </EditField>
                    </>
                  )}
                </>
              ) : (
                <>
                  <EditField>
                    <EditLabel>Model</EditLabel>
                    <ModelPicker
                      provider={editForm.provider}
                      onProviderChange={handleProviderChange}
                      models={pickerModels}
                      loading={pickerLoading}
                      selectedModelId={editForm.modelId}
                      onSelect={handlePickModel}
                      suggestions={suggestions}
                      filterHint={requiresTools && editForm.provider === 'OpenRouter' ? 'Function-calling models only' : null}
                    />
                  </EditField>

                  <EditField>
                    <EditLabel>Max Tokens</EditLabel>
                    <EditInput
                      type="number"
                      value={editForm.maxTokens}
                      onChange={e => setEditForm(f => ({ ...f, maxTokens: parseInt(e.target.value) || 0 }))}
                    />
                    <QuickPickRow>
                      {[1024, 2048, 4096, 8192, 16384].map(v => (
                        <QuickPickBtn key={v} $active={editForm.maxTokens === v}
                          onClick={() => setEditForm(f => ({ ...f, maxTokens: v }))}>
                          {v >= 1048576 ? `${v / 1048576}M` : v >= 1024 ? `${v / 1024}k` : v}
                        </QuickPickBtn>
                      ))}
                    </QuickPickRow>
                    <QuickPickRow>
                      {[32768, 65536, 131072, 262144, 524288, 1048576].map(v => (
                        <QuickPickBtn key={v} $active={editForm.maxTokens === v}
                          onClick={() => setEditForm(f => ({ ...f, maxTokens: v }))}>
                          {v >= 1048576 ? `${v / 1048576}M` : v >= 1024 ? `${v / 1024}k` : v}
                        </QuickPickBtn>
                      ))}
                    </QuickPickRow>
                  </EditField>

                  <EditField>
                    <EditLabel>Temperature</EditLabel>
                    <EditInput
                      type="number" step="0.1" min="0" max="1"
                      value={editForm.temperature}
                      onChange={e => setEditForm(f => ({ ...f, temperature: parseFloat(e.target.value) || 0 }))}
                    />
                  </EditField>

                  <EditField>
                    <EditLabel>Pricing<AutoNote>auto-filled from {editForm.provider}</AutoNote></EditLabel>
                    <PricingRow>
                      <PriceChip>
                        <PriceChipLabel>Input</PriceChipLabel>
                        <PriceChipValue>{formatTokenPrice(editForm.inputPricePerMillionTokens)}</PriceChipValue>
                        {editForm.inputPricePerMillionTokens > 0 && <PriceChipUnit>per 1M tokens</PriceChipUnit>}
                      </PriceChip>
                      <PriceChip>
                        <PriceChipLabel>Output</PriceChipLabel>
                        <PriceChipValue>{formatTokenPrice(editForm.outputPricePerMillionTokens)}</PriceChipValue>
                        {editForm.outputPricePerMillionTokens > 0 && <PriceChipUnit>per 1M tokens</PriceChipUnit>}
                      </PriceChip>
                    </PricingRow>
                    <OverrideToggle type="button" onClick={() => setShowOverride(v => !v)}>
                      {showOverride ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                      Override pricing (advanced)
                    </OverrideToggle>
                    {showOverride && (
                      <>
                        <EditField style={{ marginTop: 8 }}>
                          <EditLabel>Input Price ($/1M tokens)</EditLabel>
                          <EditInput
                            type="number" step="0.01" min="0"
                            value={editForm.inputPricePerMillionTokens ?? 0}
                            onChange={e => setEditForm(f => ({ ...f, inputPricePerMillionTokens: parseFloat(e.target.value) || 0 }))}
                          />
                        </EditField>
                        <EditField>
                          <EditLabel>Output Price ($/1M tokens)</EditLabel>
                          <EditInput
                            type="number" step="0.01" min="0"
                            value={editForm.outputPricePerMillionTokens ?? 0}
                            onChange={e => setEditForm(f => ({ ...f, outputPricePerMillionTokens: parseFloat(e.target.value) || 0 }))}
                          />
                        </EditField>
                      </>
                    )}
                  </EditField>
                </>
              )}
            </ModalBody>
            <ModalFooter>
              <Button
                variant="secondary" size="sm" style={{ marginRight: 'auto' }}
                disabled={testing === 'modal' || !editForm.modelId}
                onClick={() => handleTest({ id: 'modal', modelId: editForm.modelId, provider: editForm.provider, displayName: editingConfig.displayName })}
              >
                {testing === 'modal' ? 'Testing…' : 'Test model'}
              </Button>
              <Button variant="secondary" size="sm" onClick={() => setEditingConfig(null)}>Cancel</Button>
              <Button variant="primary" size="sm" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
      )}
    </>
  );
};

// --- MCP Tools Data ---

const MCP_TOOLS = [
  { name: 'list_tasks', description: 'List support tasks with filters (status, priority, project scope, search)', category: 'Tasks' },
  { name: 'get_task', description: 'Get detailed task info including comments, attachments, and relations', category: 'Tasks' },
  { name: 'update_task_status', description: 'Change task status (Open, InProgress, OnHold, Resolved, Closed, Backlog)', category: 'Tasks' },
  { name: 'update_task_priority', description: 'Change task priority (Low, Medium, High, Critical)', category: 'Tasks' },
  { name: 'add_task_comment', description: 'Add a comment to a task', category: 'Tasks' },
  { name: 'get_task_comments', description: 'Get all comments for a task', category: 'Tasks' },
  { name: 'get_task_relations', description: 'Get related tasks (linked submissions, work orders)', category: 'Tasks' },
  { name: 'list_sentry_issues', description: 'List Sentry issues with filters (project, environment, status)', category: 'Sentry' },
  { name: 'get_sentry_issue', description: 'Get Sentry issue details with stack trace and breadcrumbs', category: 'Sentry' },
  { name: 'update_sentry_status', description: 'Update Sentry issue status (resolved, unresolved, ignored)', category: 'Sentry' },
];

const McpTab = ({ status, loading }) => {
  const theme = useTheme();
  const [generatedToken, setGeneratedToken] = useState('');
  const [copiedField, setCopiedField] = useState(null);

  const mcpEndpoint = `${import.meta.env.VITE_API_BASE_URL || 'https://api-develop.aqrate.ca'}/mcp`;

  const generateToken = () => {
    const bytes = new Uint8Array(20);
    crypto.getRandomValues(bytes);
    const hex = Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join('');
    setGeneratedToken(`mcp_${hex}`);
  };

  const copyToClipboard = async (text, field) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
    }
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const claudeCodeConfig = JSON.stringify({
    mcpServers: {
      aqrate: {
        type: 'sse',
        url: mcpEndpoint,
        headers: { Authorization: `Bearer ${generatedToken || '<your-token>'}` }
      }
    }
  }, null, 2);

  if (loading) return null;

  return (
    <div>
      {/* Status */}
      <CardGrid>
        <Card>
          <CardHeader>
            <CardTitle><Plug size={18} color={theme.colors.muted} /> <ThemedText style={{ fontWeight: 600 }}>Server Status</ThemedText></CardTitle>
          </CardHeader>
          <CardBody>
            <InfoRow>
              <InfoLabel>Status</InfoLabel>
              <span style={{
                padding: '2px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600,
                background: status?.enabled ? '#dcfce7' : '#fee2e2',
                color: status?.enabled ? '#166534' : '#991b1b'
              }}>{status?.enabled ? 'Enabled' : 'Disabled'}</span>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Token</InfoLabel>
              <span style={{
                padding: '2px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600,
                background: status?.tokenConfigured ? '#dcfce7' : '#fef3c7',
                color: status?.tokenConfigured ? '#166534' : '#92400e'
              }}>{status?.tokenConfigured ? 'Configured' : 'Not Configured'}</span>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Endpoint</InfoLabel>
              <code style={{ fontSize: 13, background: theme.colors.background, padding: '2px 8px', borderRadius: 4, border: `1px solid ${theme.colors.border}` }}>{mcpEndpoint}</code>
            </InfoRow>
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle><Shield size={18} color={theme.colors.muted} /> <ThemedText style={{ fontWeight: 600 }}>Token Generator</ThemedText></CardTitle>
          </CardHeader>
          <CardBody>
            <p style={{ fontSize: 13, color: theme.colors.muted, marginBottom: 12, lineHeight: 1.5 }}>
              Generate a secure API token for MCP authentication. Add to <code style={{ fontSize: 12, background: theme.colors.surface, padding: '1px 4px', borderRadius: 4 }}>appsettings.json</code> under <code style={{ fontSize: 12, background: theme.colors.surface, padding: '1px 4px', borderRadius: 4 }}>ProjectConfig:McpServer:ApiToken</code>.
            </p>
            <Button onClick={generateToken} style={{ marginBottom: generatedToken ? 12 : 0 }}>Generate Token</Button>
            {generatedToken && (
              <div style={{ padding: 12, background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 8, marginTop: 12 }}>
                <p style={{ fontSize: 13, fontWeight: 500, color: '#92400e', marginBottom: 8 }}>Save this token now — it won't be shown again after you leave this page.</p>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <code style={{ flex: 1, fontSize: 13, background: '#fff', padding: '8px 12px', borderRadius: 4, border: '1px solid #fcd34d', fontFamily: 'monospace', wordBreak: 'break-all' }}>{generatedToken}</code>
                  <button onClick={() => copyToClipboard(generatedToken, 'token')} style={{ flexShrink: 0, padding: 8, background: 'none', border: `1px solid ${theme.colors.border}`, borderRadius: 4, cursor: 'pointer', display: 'flex' }}>
                    {copiedField === 'token' ? <Check size={16} color="#16a34a" /> : <Copy size={16} color={theme.colors.muted} />}
                  </button>
                </div>
              </div>
            )}
          </CardBody>
        </Card>
      </CardGrid>

      {/* Connection Instructions */}
      <Card style={{ marginTop: theme.spacing.lg }}>
        <CardHeader>
          <CardTitle><Terminal size={18} color={theme.colors.muted} /> <ThemedText style={{ fontWeight: 600 }}>Connection Instructions</ThemedText></CardTitle>
        </CardHeader>
        <CardBody>
          <div style={{ marginBottom: 16 }}>
            <p style={{ fontSize: 14, fontWeight: 600, color: theme.colors.text, marginBottom: 8 }}>1. Add to Claude Code settings</p>
            <p style={{ fontSize: 13, color: theme.colors.muted, marginBottom: 8 }}>
              Add the following to your <code style={{ fontSize: 12, background: theme.colors.surface, padding: '1px 4px', borderRadius: 4 }}>~/.claude/settings.json</code> or project <code style={{ fontSize: 12, background: theme.colors.surface, padding: '1px 4px', borderRadius: 4 }}>.claude/settings.json</code>:
            </p>
            <div style={{ position: 'relative' }}>
              <pre style={{ fontSize: 13, background: '#1e1e2e', color: '#a6e3a1', padding: 16, borderRadius: 8, overflow: 'auto', fontFamily: 'monospace', lineHeight: 1.6 }}>{claudeCodeConfig}</pre>
              <button onClick={() => copyToClipboard(claudeCodeConfig, 'config')} style={{ position: 'absolute', top: 8, right: 8, padding: 6, background: 'none', border: 'none', cursor: 'pointer' }}>
                {copiedField === 'config' ? <Check size={16} color="#a6e3a1" /> : <Copy size={16} color="#6c7086" />}
              </button>
            </div>
          </div>
          <p style={{ fontSize: 14, fontWeight: 600, color: theme.colors.text, marginBottom: 4 }}>2. Configure the server</p>
          <p style={{ fontSize: 13, color: theme.colors.muted, marginBottom: 16 }}>
            Ensure <code style={{ fontSize: 12, background: theme.colors.surface, padding: '1px 4px', borderRadius: 4 }}>McpServer.Enabled</code> is true and <code style={{ fontSize: 12, background: theme.colors.surface, padding: '1px 4px', borderRadius: 4 }}>McpServer.ApiToken</code> matches the token in both files.
          </p>
          <p style={{ fontSize: 14, fontWeight: 600, color: theme.colors.text, marginBottom: 4 }}>3. Verify connection</p>
          <p style={{ fontSize: 13, color: theme.colors.muted }}>Restart Claude Code and verify the MCP tools are available. You should see 10 tools from the "aqrate" server.</p>
        </CardBody>
      </Card>

      {/* Available Tools */}
      <Card style={{ marginTop: theme.spacing.lg }}>
        <CardHeader>
          <CardTitle><Wrench size={18} color={theme.colors.muted} /> <ThemedText style={{ fontWeight: 600 }}>Available Tools ({MCP_TOOLS.length})</ThemedText></CardTitle>
        </CardHeader>
        <CardBody style={{ padding: 0 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${theme.colors.border}` }}>
                <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600, color: theme.colors.muted, fontSize: 12, textTransform: 'uppercase' }}>Tool</th>
                <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600, color: theme.colors.muted, fontSize: 12, textTransform: 'uppercase' }}>Category</th>
                <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600, color: theme.colors.muted, fontSize: 12, textTransform: 'uppercase' }}>Description</th>
              </tr>
            </thead>
            <tbody>
              {MCP_TOOLS.map(tool => (
                <tr key={tool.name} style={{ borderBottom: `1px solid ${theme.colors.border}20` }}>
                  <td style={{ padding: '10px 16px' }}>
                    <code style={{ fontSize: 13, fontFamily: 'monospace', background: theme.colors.surface || theme.colors.background, padding: '2px 8px', borderRadius: 4, border: `1px solid ${theme.colors.border}` }}>{tool.name}</code>
                  </td>
                  <td style={{ padding: '10px 16px' }}>
                    <span style={{
                      padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600,
                      background: tool.category === 'Tasks' ? '#dbeafe' : '#fce7f3',
                      color: tool.category === 'Tasks' ? '#1e40af' : '#9d174d'
                    }}>{tool.category}</span>
                  </td>
                  <td style={{ padding: '10px 16px', color: theme.colors.muted }}>{tool.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardBody>
      </Card>
    </div>
  );
};

// --- Component ---

export const DeveloperToolsPage = () => {
  const theme = useTheme();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'services');

  // Services tab state
  const [serverData, setServerData] = useState(null);
  const [pdfData, setPdfData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);

  // New tab states
  const [connectivityData, setConnectivityData] = useState(null);
  const [connectivityLoading, setConnectivityLoading] = useState(false);
  const [jobsData, setJobsData] = useState(null);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [pdfQueueData, setPdfQueueData] = useState(null);
  const [pdfQueueLoading, setPdfQueueLoading] = useState(false);
  const [configData, setConfigData] = useState(null);
  const [configLoading, setConfigLoading] = useState(false);
  const [errorsData, setErrorsData] = useState(null);
  const [errorsLoading, setErrorsLoading] = useState(false);
  const [sentryData, setSentryData] = useState(null);
  const [sentryLoading, setSentryLoading] = useState(false);
  const [sentryFilters, setSentryFilters] = useState({ project: null, query: 'is:unresolved', statsPeriod: '14d', sort: 'date', searchText: '' });

  // AI Models tab state
  const [aiConfigs, setAiConfigs] = useState(null);
  const [aiAvailableModels, setAiAvailableModels] = useState(null);
  const [aiModelsLoading, setAiModelsLoading] = useState(false);
  const [openRouterModels, setOpenRouterModels] = useState(null);
  const [openRouterModelsLoading, setOpenRouterModelsLoading] = useState(false);

  // MCP tab state
  const [mcpStatus, setMcpStatus] = useState(null);
  const [mcpLoading, setMcpLoading] = useState(false);

  // Track which tabs have been fetched
  const fetchedTabs = useRef(new Set(['services']));

  const fetchServices = useCallback(async () => {
    setLoading(true);
    try {
      setFetchError(null);
      const [serverRes, pdfRes] = await Promise.all([
        systemStatusService.getServerStatus(),
        systemStatusService.getPdfServiceStatus(),
      ]);
      setServerData(serverRes);
      setPdfData(pdfRes);
    } catch (error) {
      setFetchError(error?.response?.data?.message || 'Failed to load developer tools data. Please check your connection and try again.');
      toast.error('Failed to fetch service status');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchConnectivity = useCallback(async () => {
    setConnectivityLoading(true);
    try {
      const res = await systemStatusService.getConnectivity();
      setConnectivityData(res);
    } catch {
      toast.error('Failed to fetch connectivity data');
    } finally {
      setConnectivityLoading(false);
    }
  }, []);

  const fetchJobs = useCallback(async () => {
    setJobsLoading(true);
    try {
      const res = await systemStatusService.getBackgroundJobs();
      setJobsData(res);
    } catch {
      toast.error('Failed to fetch background jobs');
    } finally {
      setJobsLoading(false);
    }
  }, []);

  const fetchPdfQueue = useCallback(async () => {
    setPdfQueueLoading(true);
    try {
      const res = await systemStatusService.getPdfQueue();
      setPdfQueueData(res);
    } catch {
      toast.error('Failed to fetch PDF queue');
    } finally {
      setPdfQueueLoading(false);
    }
  }, []);

  const fetchConfig = useCallback(async () => {
    setConfigLoading(true);
    try {
      const res = await systemStatusService.getConfiguration();
      setConfigData(res);
    } catch {
      toast.error('Failed to fetch configuration');
    } finally {
      setConfigLoading(false);
    }
  }, []);

  const fetchErrors = useCallback(async () => {
    setErrorsLoading(true);
    try {
      const res = await systemStatusService.getRecentErrors();
      setErrorsData(res);
    } catch {
      toast.error('Failed to fetch recent errors');
    } finally {
      setErrorsLoading(false);
    }
  }, []);

  const clearErrors = useCallback(async () => {
    try {
      await systemStatusService.clearRecentErrors();
      toast.success('Error log cleared');
      fetchErrors();
    } catch {
      toast.error('Failed to clear errors');
    }
  }, [fetchErrors]);

  const fetchSentryIssues = useCallback(async (filters) => {
    const f = filters || sentryFilters;
    setSentryLoading(true);
    try {
      const params = {};
      if (f.project) params.project = f.project;
      if (f.query) params.query = f.query;
      if (f.statsPeriod) params.statsPeriod = f.statsPeriod;
      if (f.sort) params.sort = f.sort;
      const res = await systemStatusService.getSentryIssues(params);
      setSentryData(res);
    } catch {
      toast.error('Failed to fetch Sentry issues');
    } finally {
      setSentryLoading(false);
    }
  }, [sentryFilters]);

  const fetchAIModels = useCallback(async () => {
    setAiModelsLoading(true);
    try {
      const [configs, models] = await Promise.all([
        aiModelService.getConfigs(),
        aiModelService.getAvailableModels('Bedrock'),
      ]);
      setAiConfigs(configs);
      setAiAvailableModels(models);
    } catch {
      toast.error('Failed to fetch AI model data');
    } finally {
      setAiModelsLoading(false);
    }
  }, []);

  // OpenRouter's catalog (~300+ models) is fetched lazily — only on first switch to it in the edit modal,
  // not on every AI Models tab load like the eagerly-fetched Bedrock list above.
  const fetchOpenRouterModels = useCallback(async () => {
    if (openRouterModels || openRouterModelsLoading) return;
    setOpenRouterModelsLoading(true);
    try {
      const models = await aiModelService.getAvailableModels('OpenRouter');
      setOpenRouterModels(models);
    } catch {
      toast.error('Failed to fetch OpenRouter model catalog');
    } finally {
      setOpenRouterModelsLoading(false);
    }
  }, [openRouterModels, openRouterModelsLoading]);

  const fetchMcp = useCallback(async () => {
    setMcpLoading(true);
    try {
      const res = await systemStatusService.getMcpStatus();
      setMcpStatus(res);
    } catch {
      setMcpStatus({ enabled: false, tokenConfigured: false, endpoint: '/mcp' });
    } finally {
      setMcpLoading(false);
    }
  }, []);

  const handleSentryFilterChange = useCallback((newFilters) => {
    const prev = sentryFilters;
    setSentryFilters(newFilters);
    // Only refetch if server-side filters changed (not searchText which is client-side)
    if (prev.project !== newFilters.project || prev.query !== newFilters.query ||
        prev.statsPeriod !== newFilters.statsPeriod || prev.sort !== newFilters.sort) {
      fetchSentryIssues(newFilters);
    }
  }, [fetchSentryIssues, sentryFilters]);

  // Fetch services on mount
  useEffect(() => {
    fetchServices();
  }, [fetchServices]);

  // Lazy fetch when tab changes
  useEffect(() => {
    const fetchers = {
      connectivity: fetchConnectivity,
      'background-jobs': fetchJobs,
      'pdf-queue': fetchPdfQueue,
      configuration: fetchConfig,
      'recent-errors': fetchErrors,
      'sentry-issues': fetchSentryIssues,
      'ai-models': fetchAIModels,
      'mcp': fetchMcp,
    };

    if (fetchers[activeTab] && !fetchedTabs.current.has(activeTab)) {
      fetchedTabs.current.add(activeTab);
      fetchers[activeTab]();
    }
  }, [activeTab, fetchConnectivity, fetchJobs, fetchPdfQueue, fetchConfig, fetchErrors, fetchSentryIssues, fetchAIModels, fetchMcp]);

  // Refresh handler refreshes active tab only
  const handleRefresh = useCallback(() => {
    const refreshers = {
      services: fetchServices,
      connectivity: fetchConnectivity,
      'background-jobs': fetchJobs,
      'pdf-queue': fetchPdfQueue,
      configuration: fetchConfig,
      'recent-errors': fetchErrors,
      'sentry-issues': fetchSentryIssues,
      'ai-models': fetchAIModels,
      'mcp': fetchMcp,
    };
    refreshers[activeTab]?.();
  }, [activeTab, fetchServices, fetchConnectivity, fetchJobs, fetchPdfQueue, fetchConfig, fetchErrors, fetchSentryIssues, fetchAIModels, fetchMcp]);

  const isAnyLoading = loading || connectivityLoading || jobsLoading || pdfQueueLoading || configLoading || errorsLoading || sentryLoading || aiModelsLoading || mcpLoading;

  const serverStatus = loading ? 'loading' : serverData ? 'healthy' : 'error';
  const pdfStatus = loading ? 'loading' : (pdfData?.reachable && pdfData?.data?.status === 'healthy') ? 'healthy' : 'error';

  const tabs = [
    { key: 'services', label: 'Services' },
    { key: 'connectivity', label: 'Connectivity' },
    { key: 'background-jobs', label: 'Background Jobs' },
    { key: 'pdf-queue', label: 'PDF Queue' },
    { key: 'configuration', label: 'Configuration' },
    { key: 'recent-errors', label: 'Recent Errors' },
    { key: 'sentry-issues', label: 'Sentry Issues' },
    { key: 'ai-models', label: 'AI Models' },
    { key: 'mcp', label: 'MCP' },
  ];

  return (
    <Layout currentPath="/developer-tools">
      <PageContainer>
        <PageHeader>
          <ThemedText type="title" style={{ color: theme.colors.text }}>
            Developer Tools
          </ThemedText>
          <Button variant="secondary" size="sm" onClick={handleRefresh} disabled={isAnyLoading}>
            {isAnyLoading ? 'Refreshing...' : 'Refresh'}
          </Button>
        </PageHeader>

        <TabRow>
          {tabs.map(t => (
            <Tab key={t.key} active={activeTab === t.key} onClick={() => setActiveTab(t.key)}>
              {t.label}
            </Tab>
          ))}
        </TabRow>

        {activeTab === 'services' && (fetchError && !loading ? (
          <ErrorState
            theme={theme}
            title="Failed to load service status"
            message={fetchError}
            onRetry={fetchServices}
          />
        ) : (
          <CardGrid>
            {/* .NET API Server Card */}
            <Card>
              <CardHeader>
                <CardTitle>
                  <StatusDot $status={serverStatus} />
                  <ThemedText type="defaultSemiBold" style={{ color: theme.colors.text }}>
                    .NET API Server
                  </ThemedText>
                </CardTitle>
                <ThemedText type="small" style={{ color: theme.colors.muted }}>
                  {serverStatus === 'healthy' ? 'Online' : serverStatus === 'loading' ? 'Checking...' : 'Offline'}
                </ThemedText>
              </CardHeader>
              <CardBody>
                {serverData ? (
                  <>
                    <InfoRow><InfoLabel>Version</InfoLabel><InfoValue>{serverData.version}</InfoValue></InfoRow>
                    <InfoRow><InfoLabel>Environment</InfoLabel><InfoValue>{serverData.environment}</InfoValue></InfoRow>
                    <InfoRow><InfoLabel>Uptime</InfoLabel><InfoValue>{formatUptime(serverData.uptime)}</InfoValue></InfoRow>
                    <InfoRow><InfoLabel>.NET Version</InfoLabel><InfoValue>{serverData.dotnetVersion}</InfoValue></InfoRow>
                    <InfoRow><InfoLabel>Working Set</InfoLabel><InfoValue>{formatBytes(serverData.memory?.workingSet)}</InfoValue></InfoRow>
                    <InfoRow><InfoLabel>GC Memory</InfoLabel><InfoValue>{formatBytes(serverData.memory?.gcTotalMemory)}</InfoValue></InfoRow>
                    <InfoRow><InfoLabel>GC Collections</InfoLabel><InfoValue>Gen0: {serverData.memory?.gcGen0Collections} / Gen1: {serverData.memory?.gcGen1Collections} / Gen2: {serverData.memory?.gcGen2Collections}</InfoValue></InfoRow>
                    <InfoRow><InfoLabel>OS</InfoLabel><InfoValue>{serverData.osDescription}</InfoValue></InfoRow>
                    <InfoRow><InfoLabel>PID</InfoLabel><InfoValue>{serverData.processId}</InfoValue></InfoRow>
                    <InfoRow><InfoLabel>CPU Cores</InfoLabel><InfoValue>{serverData.processorCount}</InfoValue></InfoRow>
                  </>
                ) : !loading ? (
                  <ErrorMessage>Could not reach API server</ErrorMessage>
                ) : null}
              </CardBody>
              {serverData?.timestamp && (
                <LastChecked>Last checked: {new Date(serverData.timestamp).toLocaleTimeString()}</LastChecked>
              )}
            </Card>

            {/* PDF Service Card */}
            <Card>
              <CardHeader>
                <CardTitle>
                  <StatusDot $status={pdfStatus} />
                  <ThemedText type="defaultSemiBold" style={{ color: theme.colors.text }}>
                    PDF Service (Node.js)
                  </ThemedText>
                </CardTitle>
                <ThemedText type="small" style={{ color: theme.colors.muted }}>
                  {pdfStatus === 'healthy' ? 'Online' : pdfStatus === 'loading' ? 'Checking...' : 'Offline'}
                </ThemedText>
              </CardHeader>
              <CardBody>
                {pdfData?.reachable && pdfData?.data ? (
                  <>
                    <InfoRow><InfoLabel>Version</InfoLabel><InfoValue>{pdfData.data.version}</InfoValue></InfoRow>
                    <InfoRow><InfoLabel>Environment</InfoLabel><InfoValue>{pdfData.data.environment}</InfoValue></InfoRow>
                    <InfoRow><InfoLabel>Uptime</InfoLabel><InfoValue>{formatUptime(pdfData.data.uptime)}</InfoValue></InfoRow>
                    <InfoRow><InfoLabel>Node.js Version</InfoLabel><InfoValue>{pdfData.data.nodeVersion}</InfoValue></InfoRow>
                    <InfoRow><InfoLabel>Heap Used</InfoLabel><InfoValue>{formatBytes(pdfData.data.memory?.heapUsed)}</InfoValue></InfoRow>
                    <InfoRow><InfoLabel>Heap Total</InfoLabel><InfoValue>{formatBytes(pdfData.data.memory?.heapTotal)}</InfoValue></InfoRow>
                    <InfoRow><InfoLabel>RSS</InfoLabel><InfoValue>{formatBytes(pdfData.data.memory?.rss)}</InfoValue></InfoRow>
                    <InfoRow><InfoLabel>OS</InfoLabel><InfoValue>{pdfData.data.os?.platform} ({pdfData.data.os?.arch})</InfoValue></InfoRow>
                    <InfoRow><InfoLabel>PID</InfoLabel><InfoValue>{pdfData.data.pid}</InfoValue></InfoRow>
                  </>
                ) : !loading ? (
                  <ErrorMessage>
                    {pdfData?.error || 'PDF service is unreachable'}
                  </ErrorMessage>
                ) : null}
              </CardBody>
              {pdfData?.checkedAt && (
                <LastChecked>Last checked: {new Date(pdfData.checkedAt).toLocaleTimeString()}</LastChecked>
              )}
            </Card>
          </CardGrid>
        ))}

        {activeTab === 'connectivity' && (
          <ConnectivityTab data={connectivityData} loading={connectivityLoading} onRefresh={fetchConnectivity} />
        )}

        {activeTab === 'background-jobs' && (
          <BackgroundJobsTab data={jobsData} loading={jobsLoading} />
        )}

        {activeTab === 'pdf-queue' && (
          <PdfQueueTab data={pdfQueueData} loading={pdfQueueLoading} onRefresh={fetchPdfQueue} />
        )}

        {activeTab === 'configuration' && (
          <ConfigurationTab data={configData} loading={configLoading} />
        )}

        {activeTab === 'recent-errors' && (
          <RecentErrorsTab data={errorsData} loading={errorsLoading} onRefresh={fetchErrors} onClear={clearErrors} />
        )}

        {activeTab === 'sentry-issues' && (
          <SentryIssuesTab
            data={sentryData}
            loading={sentryLoading}
            onRefresh={() => fetchSentryIssues()}
            onFilterChange={handleSentryFilterChange}
            filters={sentryFilters}
          />
        )}

        {activeTab === 'ai-models' && (
          <AIModelsTab
            configs={aiConfigs}
            availableModels={aiAvailableModels}
            loading={aiModelsLoading}
            onRefresh={fetchAIModels}
            openRouterModels={openRouterModels}
            openRouterModelsLoading={openRouterModelsLoading}
            onFetchOpenRouterModels={fetchOpenRouterModels}
          />
        )}

        {activeTab === 'mcp' && (
          <McpTab status={mcpStatus} loading={mcpLoading} />
        )}
      </PageContainer>
    </Layout>
  );
};
